import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import {Plus, Pencil, Trash2, X, Loader2, BookOpen, Upload} from 'lucide-react'
import { useBooks, type Book } from '@/hooks/useBooks'
import { useUpload } from '@/hooks/useUpload'
import { useAuth } from '@/hooks/useAuth'

const EMPTY_FORM: Partial<Book> = {
  title: '',
  author: '',
  description: '',
  price: 0,
  currency: 'CHF',
  status: 'available',
  stock: 0,
  order: 0,
  isbn: '',
  purchaseUrl: '',
  coverUrl: '',
}

const inputCls =
  'w-full px-4 py-2.5 bg-[#0E1B2C] border border-[#E8DCC4]/15 rounded-lg text-[#E8DCC4] placeholder:text-[#E8DCC4]/30 focus:outline-none focus:border-[#A3C644] transition-colors text-sm'
const labelCls = 'block text-[#C9A86A] text-xs uppercase tracking-widest mb-1.5'

interface FormModalProps {
  initial: Partial<Book> | null
  onClose: () => void
  onSave: (data: Partial<Book>) => Promise<void>
}

const FormModal: React.FC<FormModalProps> = ({ initial, onClose, onSave }) => {
  const [form, setForm] = useState<Partial<Book>>(initial ?? EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const { upload, uploading } = useUpload()

  const set = (k: keyof Book, v: string | number) =>
    setForm((p) => ({ ...p, [k]: v }))

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const url = await upload(file)
    if (url) set('coverUrl', url)
    else toast.error('Échec du téléversement de la couverture.')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.title?.trim() || !form.author?.trim()) {
      toast.error('Le titre et l\'auteur sont obligatoires.')
      return
    }
    if (!form.price || form.price <= 0) {
      toast.error('Le prix doit être supérieur à 0.')
      return
    }
    setSaving(true)
    try {
      await onSave(form)
      onClose()
    } catch {
      toast.error('Une erreur est survenue. Veuillez réessayer.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
      <div className="w-full max-w-2xl bg-[#14253B] rounded-2xl border border-[#E8DCC4]/10 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#E8DCC4]/10">
          <h2 className="font-serif text-xl text-[#E8DCC4]">
            {initial && (initial as Book)._id ? 'Modifier le livre' : 'Nouveau livre'}
          </h2>
          <button onClick={onClose} className="text-[#E8DCC4]/40 hover:text-[#E8DCC4] transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className={labelCls}>Titre *</label>
              <input className={inputCls} value={form.title ?? ''} onChange={(e) => set('title', e.target.value)} placeholder="Titre du livre" />
            </div>
            <div>
              <label className={labelCls}>Auteur *</label>
              <input className={inputCls} value={form.author ?? ''} onChange={(e) => set('author', e.target.value)} placeholder="Nom de l'auteur" />
            </div>
            <div>
              <label className={labelCls}>ISBN</label>
              <input className={inputCls} value={form.isbn ?? ''} onChange={(e) => set('isbn', e.target.value)} placeholder="978-..." />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Description</label>
              <textarea
                className={inputCls + ' resize-none'}
                rows={3}
                value={form.description ?? ''}
                onChange={(e) => set('description', e.target.value)}
                placeholder="Résumé du livre..."
              />
            </div>
            <div>
              <label className={labelCls}>Prix *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className={inputCls}
                value={form.price ?? 0}
                onChange={(e) => set('price', parseFloat(e.target.value) || 0)}
              />
            </div>
            <div>
              <label className={labelCls}>Devise</label>
              <select className={inputCls} value={form.currency ?? 'CHF'} onChange={(e) => set('currency', e.target.value)}>
                <option value="CHF">CHF</option>
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Statut</label>
              <select className={inputCls} value={form.status ?? 'available'} onChange={(e) => set('status', e.target.value)}>
                <option value="available">Disponible</option>
                <option value="sold_out">Épuisé</option>
                <option value="hidden">Masqué</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Stock</label>
              <input
                type="number"
                min="0"
                className={inputCls}
                value={form.stock ?? 0}
                onChange={(e) => set('stock', parseInt(e.target.value) || 0)}
              />
            </div>
            <div>
              <label className={labelCls}>Ordre d'affichage</label>
              <input
                type="number"
                className={inputCls}
                value={form.order ?? 0}
                onChange={(e) => set('order', Number(e.target.value))}
              />
            </div>
            <div>
              <label className={labelCls}>URL d'achat externe</label>
              <input className={inputCls} value={form.purchaseUrl ?? ''} onChange={(e) => set('purchaseUrl', e.target.value)} placeholder="https://..." />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Image de couverture</label>
              <div className="flex gap-3 items-center">
                <label className="cursor-pointer flex items-center gap-2 px-4 py-2.5 bg-[#0E1B2C] border border-[#E8DCC4]/15 rounded-lg text-[#E8DCC4]/70 text-sm hover:border-[#A3C644] transition-colors">
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Envoi en cours...' : 'Téléverser une image'}
                  <input type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} />
                </label>
                {form.coverUrl && (
                  <div className="flex items-center gap-2">
                    <img src={form.coverUrl} alt="Couverture" className="w-12 h-12 object-cover rounded-lg border border-[#E8DCC4]/20" />
                    <span className="text-[#A3C644] text-xs">✓ Image ajoutée</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 border border-[#E8DCC4]/15 text-[#E8DCC4]/70 rounded-lg hover:border-[#E8DCC4]/40 transition-colors text-sm">
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving || uploading}
              className="px-5 py-2.5 bg-[#A3C644] text-[#0E1B2C] font-semibold rounded-lg hover:bg-[#b4d655] transition-colors disabled:opacity-60 flex items-center gap-2 text-sm"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {initial && (initial as Book)._id ? 'Enregistrer' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const BooksManager: React.FC = () => {
  const { books, loading, fetchBooks, createBook, updateBook, deleteBook } = useBooks()
  const { user } = useAuth()
  const [modal, setModal] = useState<Partial<Book> | null | false>(false)

  useEffect(() => {
    fetchBooks()
  }, [fetchBooks])

  const handleSave = async (data: Partial<Book>) => {
    if ((data as Book)._id) {
      await updateBook((data as Book)._id, data)
      toast.success('Livre mis à jour.')
    } else {
      await createBook({ ...data, creator: user?.userId ?? 'admin' })
      toast.success('Livre créé.')
    }
    await fetchBooks()
  }

  const handleDelete = async (book: Book) => {
    if (!confirm(`Supprimer "${book.title}" ?`)) return
    try {
      await deleteBook(book._id)
      toast.success('Livre supprimé.')
      await fetchBooks()
    } catch {
      toast.error('Erreur lors de la suppression.')
    }
  }

  const statusBadge = (status: string) => {
    if (status === 'available') return 'bg-[#A3C644]/20 text-[#A3C644] border-[#A3C644]/30'
    if (status === 'sold_out') return 'bg-red-400/20 text-red-400 border-red-400/30'
    return 'bg-[#E8DCC4]/10 text-[#E8DCC4]/40 border-[#E8DCC4]/20'
  }

  const statusLabel = (status: string) => {
    if (status === 'available') return 'Disponible'
    if (status === 'sold_out') return 'Épuisé'
    return 'Masqué'
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-2xl text-[#E8DCC4]">Livres en vente</h2>
        <button
          onClick={() => setModal(EMPTY_FORM)}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#A3C644] text-[#0E1B2C] font-semibold rounded-lg hover:bg-[#b4d655] transition-colors text-sm"
        >
          <Plus className="w-4 h-4" />
          Nouveau livre
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-[#A3C644]" />
        </div>
      ) : books.length === 0 ? (
        <div className="text-center py-16 text-[#E8DCC4]/40">
          <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Aucun livre. Ajoutez-en un.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {books.map((book) => (
            <div key={book._id} className="flex items-start gap-4 p-4 bg-[#0E1B2C] rounded-xl border border-[#E8DCC4]/10 hover:border-[#E8DCC4]/20 transition-colors">
              {book.coverUrl ? (
                <img src={book.coverUrl} alt={book.title} className="w-14 h-18 object-cover rounded-lg flex-shrink-0 border border-[#E8DCC4]/10" />
              ) : (
                <div className="w-14 h-14 rounded-lg flex-shrink-0 bg-[#14253B] border border-[#E8DCC4]/10 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-[#E8DCC4]/20" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-medium text-[#E8DCC4] text-sm">{book.title}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${statusBadge(book.status)}`}>
                    {statusLabel(book.status)}
                  </span>
                </div>
                <p className="text-[#C9A86A] text-xs mb-1">{book.author}</p>
                {book.description && (
                  <p className="text-[#E8DCC4]/50 text-xs line-clamp-2">{book.description}</p>
                )}
                <p className="text-[#E8DCC4]/60 text-xs mt-1 font-medium">
                  {book.price} {book.currency ?? 'CHF'}
                  {book.stock !== undefined && (
                    <span className="text-[#E8DCC4]/30 ml-2">· Stock : {book.stock}</span>
                  )}
                </p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => setModal(book)}
                  className="p-2 text-[#E8DCC4]/40 hover:text-[#C9A86A] transition-colors"
                  title="Modifier"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(book)}
                  className="p-2 text-[#E8DCC4]/40 hover:text-red-400 transition-colors"
                  title="Supprimer"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal !== false && (
        <FormModal
          initial={modal}
          onClose={() => setModal(false)}
          onSave={handleSave}
        />
      )}
    </div>
  )
}

export default BooksManager
